import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { invoicesAPI } from "../../../api/apiClient";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faSpinner,
    faSearch,
    faTimes
} from "@fortawesome/free-solid-svg-icons";

import "../../../css/Views/InvoiceBankTransfersSection.css";

const InvoiceBankTransfersSection = ({ document, onChanged }) => {
    const { callApi } = useApi();

    const [availableTransfers, setAvailableTransfers] = useState([]);
    const [showAddTransfer, setShowAddTransfer] = useState(false);

    const [searchTerm, setSearchTerm] = useState("");

    const [isLoadingTransfers, setIsLoadingTransfers] = useState(false);
    const [addingTransferId, setAddingTransferId] = useState(null);
    const [removingTransferId, setRemovingTransferId] = useState(null);

    const formatDate = (value) => {
        if (!value)
            return "—";

        return new Date(value).toLocaleDateString("pl-PL");
    };

    const formatAmount = (amount) => {
        if (amount == null)
            return "—";

        return new Intl.NumberFormat("pl-PL", {
            style: "currency",
            currency: document.currency || "PLN"
        }).format(Math.abs(amount));
    };

    const loadAvailableTransfers = () => {
        setIsLoadingTransfers(true);

        callApi(
            () => invoicesAPI.getAvailableTransfers(),
            (data) => {
                setAvailableTransfers(Array.isArray(data) ? data : []);
                setIsLoadingTransfers(false);
            },
            () => {
                setIsLoadingTransfers(false);
            }
        );
    };

    useEffect(() => {
        if (showAddTransfer)
            loadAvailableTransfers();
    }, [showAddTransfer]);

    const filteredTransfers = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();

        if (!search)
            return availableTransfers;

        const normalizedSearch = search.replace(",", ".");

        return availableTransfers.filter((transfer) => {
            const amount = Math.abs(transfer.amount ?? 0);
            const amountText = amount.toFixed(2);
            const amountShortText = amount.toString();
            const contractor = transfer.contrahent?.toLowerCase() ?? "";
            const title = transfer.title?.toLowerCase() ?? "";
            const date = formatDate(transfer.transferDate).toLowerCase();

            return (
                contractor.includes(search) ||
                title.includes(search) ||
                date.includes(search) ||
                amountText.includes(normalizedSearch) ||
                amountShortText.includes(normalizedSearch)
            );
        });
    }, [availableTransfers, searchTerm]);

    const handleAddTransfer = (bankTransferId) => {
        if (addingTransferId != null)
            return;

        setAddingTransferId(bankTransferId);

        callApi(
            () => invoicesAPI.addBankTransfer(document.id, bankTransferId),
            () => {
                onChanged?.(() => {
                    setAddingTransferId(null);
                    setSearchTerm("");
                    setShowAddTransfer(false);
                });
            },
            () => {
                setAddingTransferId(null);
            }
        );
    };

    const handleRemoveTransfer = (bankTransferId) => {
        if (removingTransferId != null)
            return;

        setRemovingTransferId(bankTransferId);

        callApi(
            () => invoicesAPI.removeBankTransfer(document.id, bankTransferId),
            () => {
                onChanged?.(() => {
                    setRemovingTransferId(null);
                });
            },
            () => {
                setRemovingTransferId(null);
            }
        );
    };

    const closeSearch = () => {
        if (addingTransferId != null)
            return;

        setShowAddTransfer(false);
        setSearchTerm("");
    };

    const linkedAmount = document.linkedTransfersAmount ?? 0;

    return (
        <div className="invoice-transfers-section">

            {/* NAGŁÓWEK */}

            <div className="invoice-section-header">
                <div className="invoice-transfers-heading">
                    <h3>Powiązane przelewy</h3>

                    <span className="invoice-transfers-total">
                        {formatAmount(linkedAmount)}
                    </span>
                </div>

                {!showAddTransfer && (
                    <button className="invoice-transfer-action invoice-transfer-action-primary" onClick={() => setShowAddTransfer(true)}>
                        + Dodaj przelew
                    </button>
                )}
            </div>

            {document.bankTransfers?.length > 0 ? (
                <div className="invoice-linked-transfers">
                    {document.bankTransfers.map((transfer) => {
                        const isRemoving = removingTransferId === transfer.id;

                        return (
                            <div key={transfer.id} className="invoice-linked-transfer">
                                <div className="invoice-linked-transfer-main">
                                    <div className="invoice-linked-transfer-top">
                                        <span className="invoice-linked-transfer-date">
                                            {formatDate(transfer.date)}
                                        </span>

                                        <span className="invoice-linked-transfer-amount">
                                            {formatAmount(transfer.amount)}
                                        </span>
                                    </div>

                                    <div className="invoice-linked-transfer-contractor">
                                        {transfer.contrahent || "Brak kontrahenta"}
                                    </div>

                                    {transfer.title && (
                                        <div className="invoice-linked-transfer-title">
                                            {transfer.title}
                                        </div>
                                    )}
                                </div>

                                <button className={`invoice-transfer-action invoice-transfer-action-danger ${isRemoving ? "invoice-transfer-action-disabled" : ""}`} onClick={() => {
                                    if (!isRemoving)
                                        handleRemoveTransfer(transfer.id);
                                }}>
                                    {isRemoving ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin />
                                            Odpinanie...
                                        </>
                                    ) : (
                                        "Odepnij"
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="invoice-transfers-empty">
                    Brak powiązanych przelewów.
                </div>
            )}

            {showAddTransfer && (
                <div className="invoice-transfer-search-panel">

                    <div className="invoice-transfer-search-header">
                        <span>Wybierz przelew do podpięcia</span>

                        <div className="invoice-transfer-search-close" onClick={closeSearch}>
                            <FontAwesomeIcon icon={faTimes} />
                        </div>
                    </div>

                    <div className="invoice-transfer-search-box">
                        <FontAwesomeIcon icon={faSearch} className="invoice-transfer-search-icon" />

                        <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Szukaj po kwocie, kontrahencie, tytule lub dacie..." autoFocus />
                    </div>

                    {isLoadingTransfers ? (
                        <div className="invoice-transfer-loading">
                            <FontAwesomeIcon icon={faSpinner} spin />
                            Ładowanie przelewów...
                        </div>
                    ) : (
                        <div className="invoice-transfer-results">
                            {filteredTransfers.length === 0 ? (
                                <div className="invoice-transfer-no-results">
                                    Brak pasujących przelewów.
                                </div>
                            ) : (
                                filteredTransfers.map((transfer) => {
                                    const isAdding = addingTransferId === transfer.id;
                                    const isDisabled = addingTransferId != null;

                                    return (
                                        <div
                                            key={transfer.id}
                                            className={`invoice-transfer-result ${isAdding ? "invoice-transfer-result-selected" : ""} ${isDisabled ? "invoice-transfer-result-disabled" : ""}`}
                                            onClick={() => {
                                                if (!isDisabled)
                                                    handleAddTransfer(transfer.id);
                                            }}
                                        >
                                            <div className="invoice-transfer-result-row">
                                                <div className="invoice-transfer-result-contractor">
                                                    {transfer.contrahent || "Brak kontrahenta"}
                                                </div>

                                                <div className="invoice-transfer-result-date">
                                                    {formatDate(transfer.transferDate)}
                                                </div>
                                            </div>

                                            <div className="invoice-transfer-result-row">
                                                <div className="invoice-transfer-result-title">
                                                    {transfer.title || "Brak tytułu"}
                                                </div>

                                                <div className="invoice-transfer-result-amount">
                                                    {isAdding ? (
                                                        <>
                                                            <FontAwesomeIcon icon={faSpinner} spin />
                                                            {" "}Dodawanie...
                                                        </>
                                                    ) : (
                                                        formatAmount(transfer.amount)
                                                    )}
                                                </div>
                                            </div>

                                            {transfer.linkedDocumentsCount > 0 && (
                                                <div className="invoice-transfer-result-linked">
                                                    Powiązany z {transfer.linkedDocumentsCount} dokumentami
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    )}

                </div>
            )}
        </div>
    );
};

export default InvoiceBankTransfersSection;